/**
 * Starting an optimize-campaign run — the one implementation.
 *
 * This is what the "Optimize Campaign" button does, extracted so that every
 * caller triggers optimization the same way: the campaign details page, the
 * campaign grid's detail panel, and the sidekick Optimization agent.
 *
 * It was three copies before, and the divergence was not cosmetic. Each one had
 * to independently remember:
 *
 *   · the ACCOUNT GATE — optimization is opt-in per platform via the
 *     `optimize-campaign` account setting. A caller that forgets it offers a
 *     button that dispatches a run the account never enabled.
 *   · the platformCampaignId DERIVATION — `platform_campaign_id` normally, but
 *     falling back to `advertiser_id` on the older platforms where that column
 *     really does hold a campaign id. LinkedIn / PMax / DemandGen must NOT fall
 *     back: their advertiser_id is an ACCOUNT id, and sending it as a campaign id
 *     queries a campaign that does not exist.
 *   · MADHIVE needing a reporting-metrics upload FIRST. A MadHive run dispatched
 *     without `reportingMetricsPath` has no delivery data to analyse.
 *
 * A caller that goes straight to `POST /campaigns/:id/optimize` skips all three.
 * Use `startOptimizeRun` and the helpers here instead.
 */

import { api, getApiUrl } from './apiConfig';
import { advertiserIdIsAccountId, isOptimizeCampaignEnabledFor } from './optimizeCampaignGating';
import { canonicalPlatform } from './accountSettingPlatformList';

/**
 * Is optimization enabled for this campaign's platform on this account?
 *
 * `campaign` needs only `destination`; `accountSettings` is the raw settings map.
 * Returns false when either is missing rather than defaulting open — an unknown
 * account setting must not read as "enabled".
 */
export function optimizeEnabledFor(campaign, accountSettings) {
  if (!campaign || !accountSettings) return false;
  return isOptimizeCampaignEnabledFor(campaign.destination, accountSettings['optimize-campaign']);
}

/**
 * The id to send as `platformCampaignId`, or null when the campaign has none —
 * which means it is not live on the platform and cannot be optimized.
 *
 * @param {object} campaign — needs { destination, platform_campaign_id, advertiser_id }
 */
export function resolveOptimizePlatformCampaignId(campaign) {
  if (!campaign) return null;
  // platform_campaign_id can hold a comma-separated list when one plan produced
  // several platform campaigns; the optimizer takes one, and the first is the one
  // every caller has always used.
  const stamped = String(campaign.platform_campaign_id || '').split(',')[0].trim();
  if (stamped) return stamped;
  // The fallback, and its exception. See the module comment.
  if (advertiserIdIsAccountId(campaign.destination)) return null;
  return campaign.advertiser_id || null;
}

/**
 * Does this platform need a reporting-metrics file uploaded before a run?
 *
 * MadHive alone: its optimizer reads delivery data from an uploaded ZIP rather
 * than pulling it from an API, so the flow is upload → then dispatch. Callers
 * that cannot host that upload should say so rather than dispatching a run with
 * nothing to analyse.
 */
export function optimizeNeedsReportingMetrics(destination) {
  return String(destination || '').toLowerCase() === 'madhive';
}

/**
 * YouTube DV360 is excluded from the optimize button everywhere it appears
 * (CampaignCompletionStatus gates on it explicitly). Kept here so a new caller
 * inherits the exclusion instead of rediscovering it.
 */
export function optimizeSupportsDestination(destination) {
  return String(destination || '').toLowerCase() !== 'youtube-dv360';
}

/**
 * Dispatch the run. Creates an optimize-campaign message via the v2 route, which
 * hands off to the v1 chat API — the same single endpoint every caller has always
 * used. Returns the message id to poll.
 *
 * @returns {Promise<{ messageId: string }>}
 * @throws  {Error} with the server's message on failure
 */
export async function startOptimizeRun({
  campaignId,
  platformCampaignId,
  reportingMetricsPath = null,
  // Optional steer for THIS run — "focus on the branded ad groups", "we care
  // about CPA not volume". The backend appends it to the optimize message as a
  // `Focus:` clause, which is the path the Python optimizer already reads it
  // from (see routes/v2/campaigns.js). Omitted entirely when blank, so a run
  // without one is byte-identical to what it was before.
  customPrompt = null,
  // Reporting window for this run — { datePreset } and/or { startDate, endDate,
  // daysBack }, as produced by utils/optimizeDateRange.buildOptimizeDateParams.
  // Omitted entirely on platforms whose optimizer reads no window, so the call
  // stays byte-identical to what it was before this existed.
  dateParams = null,
  // Which focused analysis to run: 'diagnostic' | 'pacing' | 'performance'. Null runs
  // the combined optimizer — what the "Optimize Campaign" button has always done — so a
  // caller that does not know about the focused analyses is unaffected.
  analysisKind = null,
  analysisBatchId = null,
  accountId,
  userId,
}) {
  if (!campaignId) throw new Error('campaignId is required');
  if (!platformCampaignId) {
    throw new Error(
      'Platform campaign ID not found. Please ensure the campaign has been launched successfully.',
    );
  }
  const focus = typeof customPrompt === 'string' ? customPrompt.trim() : '';
  const res = await api.post(getApiUrl(`/api/v2/campaigns/${campaignId}/optimize`), {
    platformCampaignId,
    reportingMetricsPath,
    ...(focus ? { customPrompt: focus } : {}),
    ...(analysisKind ? { analysisKind } : {}),
    ...(analysisBatchId ? { analysisBatchId } : {}),
    ...(dateParams || {}),
    accountId,
    userId,
  });
  if (!res.data?.success) {
    throw new Error(res.data?.error || 'Failed to create optimize message');
  }
  return { messageId: res.data.messageId };
}

/**
 * The focused analyses an optimizer can run, in the order they run.
 *
 * They are sequential by design: pacing and performance both assume the diagnostic has
 * already cleared the hard blockers, because an entity that cannot serve is not
 * underpacing — it is broken, and tuning advice aimed at it is wrong.
 *
 * TTD, DV360, Google Ads Search, Performance Max and Demand Gen, Facebook and LinkedIn have
 * them; every other platform runs the combined optimizer. The UI offers them as ONE button that kicks off all three (see
 * startAllFocusedAnalyses), so each entry carries only its kind and a tooltip line.
 *
 * The `title` here is the platform-neutral wording. Each supported platform answers the
 * same three questions about different objects — DV360 line items and insertion orders,
 * Search ad groups and keywords, PMax asset groups, Demand Gen ad groups and their
 * audiences and creative — so the tooltip a user actually sees comes from
 * optimizeAnalysisTitles(destination) below. A tooltip that says "line item" on a Search
 * campaign is worse than no tooltip.
 */
export const OPTIMIZE_ANALYSES = [
  { kind: 'diagnostic', title: 'Check whether this campaign can actually deliver' },
  { kind: 'pacing', title: 'Pace this campaign against its goal and recommend what closes the gap' },
  { kind: 'performance', title: 'Find efficiency and scale upside' },
];

/** Per-platform tooltip wording, keyed by canonical platform then analysis kind. */
const ANALYSIS_DESCRIPTIONS = {
  'ttd': {
    diagnostic: 'Check whether each ad group can deliver — status, creatives, flights, budgets and targeting',
    pacing: 'Compare campaign delivery with flight goals and identify pacing constraints',
    performance: 'Find efficiency and scale upside — ad group bids, KPI targets and frequency caps',
  },
  'dv360': {
    diagnostic: 'Check whether each line item can actually deliver — creatives, flight dates, budget segments, targeting',
    pacing: 'Pace each insertion order against its booked goal and recommend the changes that close the gap',
    performance: 'Find efficiency and scale upside — bids, targeting, audiences and PMP inventory',
  },
  'google-ads-search': {
    diagnostic: 'Check whether each ad group, ad and keyword can serve — approvals, paused entities, budget status, conversion tracking',
    pacing: 'Pace the campaign against its budget and say whether the budget, the auction or demand is the constraint',
    performance: 'Find efficiency and scale upside — budget, keywords, negatives, bidding targets and creative',
  },
  'google-ads-pmax': {
    diagnostic: 'Check whether each asset group can serve — required assets, review status, ad strength, signals, conversion tracking',
    pacing: 'Pace the campaign against its budget using days-at-cap, since Performance Max reports no impression share',
    performance: 'Find efficiency and scale upside — budget, bidding targets, search themes, audience signals and assets',
  },
  'google-ads-demandgen': {
    diagnostic: 'Check whether each ad group and ad can serve — approvals, ad strength, missing image formats, audience coverage, conversion tracking',
    pacing: 'Pace the campaign against its budget using days-at-cap, since Demand Gen reports no impression share',
    performance: 'Find efficiency and scale upside — audience segments, creative assets, budget and bidding targets',
  },
  'facebook': {
    diagnostic: 'Check whether each ad set can serve — ad review status, schedule, budget, audience size, learning phase',
    pacing: 'Pace the campaign against its budget, whether Advantage+ holds it or each ad set does',
    performance: 'Find efficiency and scale upside — budget split, bidding, audiences and creative',
  },
  'linkedin': {
    diagnostic: 'Check whether each campaign in the group can serve — draft creatives, schedule, budget, the audience-size floor',
    pacing: 'Pace against both budget levels — each campaign daily budget and the group total',
    performance: 'Find efficiency and scale upside — bids, targeting facets and creative, judged against LinkedIn norms',
  },
};

/**
 * The three tooltip lines for a campaign's platform, in run order.
 * Falls back to the neutral wording for a platform with no entry of its own.
 */
export function optimizeAnalysisTitles(destination) {
  const byKind = ANALYSIS_DESCRIPTIONS[canonicalPlatform(destination)] || {};
  return OPTIMIZE_ANALYSES.map(({ kind, title }) => byKind[kind] || title);
}

/**
 * The sentinel analysisKind the "Run Full Analysis" button sends to its page
 * handler. It never reaches the backend — the handler expands it into the three
 * real kinds via startAllFocusedAnalyses (the /optimize route 400s unknown kinds).
 */
export const ALL_ANALYSES_KIND = 'all';

/**
 * Kick off all three focused analyses as separate runs — one message and one
 * result each. Dispatched sequentially in their designed order (diagnostic →
 * pacing → performance) so the activity feed reads in that order; the runs
 * themselves execute independently once dispatched. Returns the message ids in
 * the same order, for the caller to poll.
 *
 * Takes the same params as startOptimizeRun, minus analysisKind.
 */
export async function startAllFocusedAnalyses(params) {
  const messageIds = [];
  // Persist the identity on every child message so a re-open never mixes
  // reports from different windows or concurrent full-analysis requests.
  const analysisBatchId = params.analysisBatchId || (window.crypto?.randomUUID?.()
    || `analysis-${Date.now()}-${Math.random().toString(36).slice(2)}`);
  try {
    for (const { kind } of OPTIMIZE_ANALYSES) {
      const { messageId } = await startOptimizeRun({ ...params, analysisBatchId, analysisKind: kind });
      if (!messageId) throw new Error(`${kind} analysis started but returned no message id`);
      messageIds.push(messageId);
    }
  } catch (error) {
    // A caller must keep tracking accepted jobs even if a later dispatch fails.
    error.messageIds = messageIds;
    error.analysisBatchId = analysisBatchId;
    throw error;
  }
  return { messageIds, analysisBatchId };
}

/**
 * Do the focused analyses apply to this campaign's platform?
 *
 * The platforms that have a diagnostic, a pacing and a performance launcher behind them.
 * Compared canonically, because a campaign's `destination` arrives in several conventions
 * ('search', 'g-ads-pmax', 'fb', 'meta') and a raw string compare would silently gate the
 * button off for whichever spelling was not written here.
 *
 * This list must not run ahead of the backend. A platform added here without focused
 * launchers behind it gets three buttons that each quietly run the COMBINED optimizer and
 * label the result as one of the three — worse than no buttons, because the mislabelling
 * is invisible. Add the launchers and the handler branch first.
 *
 * YouTube DV360 is deliberately absent: it shares the 'dv360' family name but not the
 * focused launchers, and it is already excluded from the Optimize button everywhere.
 * Google Ads Display is absent for the plain reason — it has no focused launchers.
 */
export function optimizeAnalysesEnabledFor(destination) {
  const platform = canonicalPlatform(destination);
  return platform === 'ttd'
    || platform === 'dv360'
    || platform === 'google-ads-search'
    || platform === 'google-ads-pmax'
    || platform === 'google-ads-demandgen'
    || platform === 'facebook'
    || platform === 'linkedin';
}

/**
 * The "Run Summary" analysis kind.
 *
 * Unlike ALL_ANALYSES_KIND this is a REAL kind that reaches the backend: the summary
 * launcher runs the three analyses itself, over one shared campaign context, and then
 * merges them into a single ranked, applyable page. So it is one message and one result,
 * not three — which is the whole difference between this button and Run Full Analysis.
 */
export const SUMMARY_KIND = 'summary';

/**
 * Is the summary run available for this campaign's platform?
 *
 * DV360 only, deliberately. The same warning on optimizeAnalysesEnabledFor applies with
 * more force here: a platform listed without a summary launcher behind it gets a button
 * whose run falls through to the COMBINED optimizer and is then labelled "Summary" — a
 * mislabelling nobody can see. The other focused platforms get this when they get a
 * summary launcher, not before.
 */
export function optimizeSummaryEnabledFor(destination) {
  return canonicalPlatform(destination) === 'dv360';
}

/** Tooltip for the Run Summary button. */
export function optimizeSummaryTitle(destination) {
  return optimizeSummaryEnabledFor(destination)
    ? 'Run all three analyses and merge them into one ranked page: what to do first, '
      + 'what it recovers, and what can be applied right here'
    : 'Not available for this platform';
}

/** Message statuses that end an optimize run, shared by every poller. */
export const OPTIMIZE_TERMINAL_STATUSES = new Set([
  'completed', 'failed', 'error', 'timeout', 'stopped',
]);

export default {
  optimizeEnabledFor,
  resolveOptimizePlatformCampaignId,
  optimizeNeedsReportingMetrics,
  optimizeSupportsDestination,
  optimizeAnalysesEnabledFor,
  optimizeAnalysisTitles,
  optimizeSummaryEnabledFor,
  optimizeSummaryTitle,
  OPTIMIZE_ANALYSES,
  ALL_ANALYSES_KIND,
  SUMMARY_KIND,
  startAllFocusedAnalyses,
  startOptimizeRun,
  OPTIMIZE_TERMINAL_STATUSES,
};
